import { useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  MapPinned,
  Share2,
  Building2,
  ExternalLink,
  Settings,
  FileText,
  Loader2,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MappingShareDialog } from "@/components/physical-mapping/MappingShareDialog";
import { CityReportShareDialog } from "@/components/physical-mapping/CityReportShareDialog";
import { usePdfGenerator } from "@/components/physical-mapping/pdf/usePdfGenerator";
import { SpotReviewTab } from "@/components/physical-mapping/spot-review/SpotReviewTab";
import { CityReviewTab } from "@/components/physical-mapping/city-review/CityReviewTab";
import { cn } from "@/lib/utils";
import {
  getDataSourceBadgeClass,
  getDataSourceLabel,
} from "@/lib/data-source";

type TabKey = "overview" | "review" | "city-review";

type MappingStatus = "not_started" | "pending" | "in_progress" | "done";

const STATUS_LABEL: Record<MappingStatus, string> = {
  not_started: "Not started",
  pending: "Pending",
  in_progress: "In progress",
  done: "Done",
};

const STATUS_STYLES: Record<MappingStatus, string> = {
  not_started: "bg-gray-100 text-gray-700 border-gray-200",
  pending: "bg-blue-100 text-blue-700 border-blue-200",
  in_progress: "bg-amber-100 text-amber-700 border-amber-200",
  done: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

function StatusChip({ status }: { status: MappingStatus }) {
  return (
    <Badge
      variant="outline"
      className={cn("text-xs font-medium", STATUS_STYLES[status])}
    >
      {STATUS_LABEL[status]}
    </Badge>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "–";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function PhysicalMappingPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    collectionId?: string;
    tab?: TabKey;
  };
  const collectionId = search.collectionId;
  const tab: TabKey =
    search.tab === "review"
      ? "review"
      : search.tab === "city-review"
        ? "city-review"
        : "overview";
  const setCollectionId = (id: string | undefined) => {
    navigate({
      search: {
        ...(id ? { collectionId: id } : {}),
        ...(tab !== "overview" ? { tab } : {}),
      },
    } as any);
  };
  const setTab = (next: TabKey) => {
    navigate({
      search: {
        ...(collectionId ? { collectionId } : {}),
        ...(next !== "overview" ? { tab: next } : {}),
      },
    } as any);
  };
  const [statusFilter, setStatusFilter] = useState<"all" | MappingStatus>("all");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [cityShareDialogOpen, setCityShareDialogOpen] = useState(false);

  const { data: collections, isLoading: loadingCollections } =
    trpc.collections.list.useQuery({});

  const { data: collection } = trpc.collections.getById.useQuery(
    { id: collectionId ?? "" },
    { enabled: !!collectionId },
  );

  const { data: stats } = trpc.collections.getMappingStats.useQuery(
    { collectionId: collectionId ?? "" },
    { enabled: !!collectionId },
  );

  const { data: locations, isLoading: loadingLocations } =
    trpc.physicalMapping.list.useQuery(
      {
        collectionId: collectionId ?? "",
        status: statusFilter === "all" ? undefined : statusFilter,
      },
      { enabled: !!collectionId },
    );

  const { data: citySummary } =
    trpc.physicalMapping.getCityReviewSummary.useQuery(
      { collectionId: collectionId ?? "" },
      { enabled: !!collectionId },
    );

  const mappingShareEnabled = collection?.mapping_share_enabled ?? false;
  const reportEnabled = citySummary?.report_enabled ?? false;

  const { isGenerating: isGeneratingPdf, generate: generatePdf } =
    usePdfGenerator({ collectionId });
  const reportToken =
    citySummary?.report_token ??
    ((collection as any)?.report_token as string | undefined) ??
    "";
  const reportUrl = reportToken
    ? `${window.location.origin}/city-review/${reportToken}`
    : "";

  const filteredCount = useMemo(() => locations?.length ?? 0, [locations]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-3 shrink-0">
        <div className="flex items-center gap-3">
          <MapPinned className="size-5 text-primary" />
          <h1 className="text-lg font-semibold">Physical Mapping</h1>
          {!loadingCollections && collections && collections.length > 0 && (
            <Select
              value={collectionId ?? ""}
              onValueChange={(v) => setCollectionId(v || undefined)}
            >
              <SelectTrigger className="w-[240px] h-8 text-sm">
                <SelectValue placeholder="Select a collection" />
              </SelectTrigger>
              <SelectContent>
                {collections.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: c.color ?? "#6b7280" }}
                      />
                      {c.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {collectionId && (
            <Tabs
              value={tab}
              onValueChange={(v) => setTab(v as TabKey)}
              className="ml-2"
            >
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="review">Spot Review</TabsTrigger>
                <TabsTrigger value="city-review">City Review</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>

        {collectionId && collection && (
          <div className="flex items-center gap-2">
            <Button
              variant={mappingShareEnabled ? "default" : "outline"}
              size="sm"
              onClick={() => setShareDialogOpen(true)}
            >
              <Share2 className="size-3.5 mr-1.5" />
              {mappingShareEnabled ? "Share enabled" : "Enable mapping share"}
            </Button>
            {reportEnabled ? (
              <div className="inline-flex">
                <Button
                  variant="default"
                  size="sm"
                  className="rounded-r-none border-r-0"
                  onClick={() => {
                    if (reportUrl) {
                      window.open(reportUrl, "_blank", "noopener,noreferrer");
                    } else {
                      setCityShareDialogOpen(true);
                    }
                  }}
                  disabled={!reportUrl}
                >
                  <Building2 className="size-3.5 mr-1.5" />
                  Open report
                  <ExternalLink className="size-3 ml-1.5 opacity-80" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="default"
                      size="sm"
                      className="rounded-l-none px-2"
                      aria-label="Report settings"
                    >
                      <ChevronDown className="size-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => setCityShareDialogOpen(true)}
                    >
                      <Settings className="size-3.5 mr-2" />
                      Share settings
                    </DropdownMenuItem>
                    {reportUrl && (
                      <DropdownMenuItem
                        onClick={() => {
                          window.open(
                            reportUrl,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                      >
                        <ExternalLink className="size-3.5 mr-2" />
                        Open report
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      disabled={isGeneratingPdf}
                      onSelect={(e) => {
                        e.preventDefault();
                        void generatePdf();
                      }}
                    >
                      {isGeneratingPdf ? (
                        <Loader2 className="size-3.5 mr-2 animate-spin" />
                      ) : (
                        <FileText className="size-3.5 mr-2" />
                      )}
                      {isGeneratingPdf
                        ? "Generating PDF…"
                        : "Create PDF report"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCityShareDialogOpen(true)}
              >
                <Building2 className="size-3.5 mr-1.5" />
                Share with city
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Empty state — no collection */}
      {!collectionId && (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <MapPinned className="size-10 mx-auto mb-3 opacity-40" />
            <p className="text-base font-medium">Select a collection</p>
            <p className="text-sm mt-1">
              Pick a collection above to manage its physical-mapping data.
            </p>
          </div>
        </div>
      )}

      {collectionId && tab === "review" && (
        <SpotReviewTab collectionId={collectionId} />
      )}

      {collectionId && tab === "city-review" && (
        <CityReviewTab collectionId={collectionId} />
      )}

      {collectionId && tab === "overview" && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Stats row */}
          <div className="grid grid-cols-4 gap-3 p-4 border-b">
            <StatCard label="Total locations" value={stats?.locations_total ?? 0} />
            <StatCard
              label="Not started"
              value={stats?.locations_not_started ?? 0}
              tone="muted"
            />
            <StatCard
              label="In progress"
              value={stats?.locations_in_progress ?? 0}
              tone="amber"
            />
            <StatCard
              label="Done"
              value={stats?.locations_done ?? 0}
              tone="emerald"
            />
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-2 px-4 py-2 border-b text-sm">
            <span className="text-muted-foreground">Filter:</span>
            {(["all", "not_started", "pending", "in_progress", "done"] as const).map(
              (s) => (
                <Button
                  key={s}
                  variant={statusFilter === s ? "default" : "ghost"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setStatusFilter(s)}
                >
                  {s === "all" ? "All" : STATUS_LABEL[s]}
                </Button>
              ),
            )}
            {!loadingLocations && (
              <span className="ml-auto text-xs text-muted-foreground">
                {filteredCount} location{filteredCount === 1 ? "" : "s"}
              </span>
            )}
          </div>

          {/* Locations table */}
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Data source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Installation spots</TableHead>
                  <TableHead>Last submitted</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingLocations ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-40" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-8" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  ))
                ) : !locations || locations.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="h-32 text-center text-muted-foreground text-sm"
                    >
                      No locations match this filter.
                    </TableCell>
                  </TableRow>
                ) : (
                  locations.map((loc: any) => (
                    <TableRow
                      key={loc.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() =>
                        navigate({
                          to: "/dashboard/physical-mapping/$locationId",
                          params: { locationId: loc.id },
                          search: { collectionId },
                        } as any)
                      }
                    >
                      <TableCell>
                        <span className="font-medium truncate block max-w-[320px]">
                          {loc.name}
                        </span>
                        {loc.address && (
                          <span className="text-xs text-muted-foreground truncate block max-w-[320px]">
                            {loc.address}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[11px] font-normal capitalize",
                            getDataSourceBadgeClass(loc.source ?? null),
                          )}
                        >
                          {getDataSourceLabel(loc.source ?? null)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          status={(loc.mapping_status ?? "not_started") as MappingStatus}
                        />
                      </TableCell>
                      <TableCell className="text-sm">
                        {loc.pil_total ?? 0}
                        {(loc.pil_total ?? 0) > 0 && (
                          <span className="text-muted-foreground">
                            {" "}
                            ({loc.pil_done ?? 0} done)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDate(loc.last_submitted_at ?? null)}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="size-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {collection && (
        <MappingShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          collectionId={collection.id}
          shareToken={collection.share_token}
          mappingShareEnabled={mappingShareEnabled}
        />
      )}

      {collection && citySummary && (
        <CityReportShareDialog
          open={cityShareDialogOpen}
          onOpenChange={setCityShareDialogOpen}
          collectionId={collection.id}
          reportToken={
            citySummary.report_token ??
            (collection as any).report_token ??
            ""
          }
          reportEnabled={citySummary.report_enabled ?? false}
          reportClosedAt={citySummary.report_closed_at ?? null}
          hubSize={citySummary.hub_size ?? null}
          title={(collection as any).report_title ?? null}
          clientName={(collection as any).report_client_name ?? null}
          intro={(collection as any).report_intro ?? null}
          logoUrl={(collection as any).report_logo_url ?? null}
        />
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  tone = "muted",
}: {
  label: string;
  value: number;
  tone?: "muted" | "amber" | "emerald";
}) {
  const toneClass =
    tone === "amber"
      ? "text-amber-700"
      : tone === "emerald"
        ? "text-emerald-700"
        : "text-foreground";
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className={cn("text-2xl font-semibold mt-1", toneClass)}>{value}</div>
    </div>
  );
}
